import type { ProviderProgressEntry } from "../../../hooks/useProviderProgress";

export interface ContextBlock {
  title: string;
  body: string;
}

export interface PhasePrompt {
  phase: string;
  receivedAt: number;
  stepNumber: number;
  systemPrompt: string;
  timestamp?: number;
}

export interface StaticSystemInstruction {
  body: string;
  mode: string | null;
  phase: string;
  receivedAt: number;
  stepNumber: number;
  timestamp?: number;
}

export function readString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((entry): entry is string => typeof entry === "string");
}

export function readContextBlocks(value: unknown): ContextBlock[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return [];
    }

    const title = readString(entry.title);
    const body = readString(entry.body);
    if (!title || !body) {
      return [];
    }

    return [{ body, title }];
  });
}

export function readPhaseValue(event: ProviderProgressEntry): string | null {
  return readString(event.event_data.phase);
}

export function getRunStartEvent(
  events: ProviderProgressEntry[],
): ProviderProgressEntry | undefined {
  return events.find((event) => event.event_type === "run:start");
}

function readPhasePromptEvent(
  event: ProviderProgressEntry,
): PhasePrompt | null {
  const phase = readString(event.event_data.phase);
  const systemPrompt = readString(event.event_data.systemPrompt);
  const { stepNumber } = event.event_data;
  if (!phase || !systemPrompt || typeof stepNumber !== "number") {
    return null;
  }

  return {
    phase,
    receivedAt: event.received_at,
    stepNumber,
    systemPrompt,
    timestamp: event.timestamp,
  };
}

export function getSystemContextBlocks(
  runStartEvent?: ProviderProgressEntry,
): ContextBlock[] {
  return readContextBlocks(runStartEvent?.event_data.systemContextBlocks);
}

export function getRawUserContextBlocks(
  runStartEvent?: ProviderProgressEntry,
): ContextBlock[] {
  return readContextBlocks(runStartEvent?.event_data.userContextBlocks);
}

export function getUserContextBlocks(
  runStartEvent?: ProviderProgressEntry,
): ContextBlock[] {
  const rawUserContextBlocks = getRawUserContextBlocks(runStartEvent);
  const unbuiltPrompt = readString(runStartEvent?.event_data.unbuiltPrompt);

  return unbuiltPrompt
    ? [{ body: unbuiltPrompt, title: "User Prompt" }, ...rawUserContextBlocks]
    : rawUserContextBlocks;
}

export function getPhasePrompts(
  events: ProviderProgressEntry[],
): PhasePrompt[] {
  const prompts: PhasePrompt[] = [];
  const seenPhases = new Set<string>();

  const sortedEvents = [...events].toSorted((eventA, eventB) => {
    const stepA =
      typeof eventA.event_data.stepNumber === "number"
        ? eventA.event_data.stepNumber
        : 0;
    const stepB =
      typeof eventB.event_data.stepNumber === "number"
        ? eventB.event_data.stepNumber
        : 0;
    return stepA - stepB;
  });

  for (const event of sortedEvents) {
    if (event.event_type !== "phase:system-prompt") {
      continue;
    }

    const phasePromptEvent = readPhasePromptEvent(event);
    if (!phasePromptEvent) {
      continue;
    }

    if (!seenPhases.has(phasePromptEvent.phase)) {
      prompts.push(phasePromptEvent);
      seenPhases.add(phasePromptEvent.phase);
      continue;
    }

    const existingPrompt = prompts.find(
      (prompt) => prompt.phase === phasePromptEvent.phase,
    );
    if (!existingPrompt) {
      continue;
    }

    existingPrompt.receivedAt = phasePromptEvent.receivedAt;
    existingPrompt.stepNumber = phasePromptEvent.stepNumber;
    existingPrompt.systemPrompt = phasePromptEvent.systemPrompt;
    existingPrompt.timestamp = phasePromptEvent.timestamp;
  }

  return prompts.toSorted(
    (promptA, promptB) => promptA.stepNumber - promptB.stepNumber,
  );
}

function buildSystemContextText(systemContextBlocks: ContextBlock[]): string {
  if (systemContextBlocks.length === 0) {
    return "";
  }

  const contextLines: string[] = ["## Execution Context"];
  for (const block of systemContextBlocks) {
    const title = block.title.trim();
    const body = block.body.trim();
    if (!title || !body) {
      continue;
    }

    contextLines.push(`### ${title}`);
    contextLines.push(body);
    contextLines.push("");
  }

  return contextLines.join("\n");
}

export function stripSystemContextFromPrompt(
  systemPrompt: string,
  systemContextBlocks: ContextBlock[],
): string {
  const trimmedSystemPrompt = systemPrompt.trim();
  const systemContextText = buildSystemContextText(systemContextBlocks);

  if (!systemContextText) {
    return trimmedSystemPrompt;
  }

  return trimmedSystemPrompt
    .replace(`${systemContextText}\n`, "")
    .replace(systemContextText, "")
    .trim();
}

export function getStaticSystemInstructions(
  events: ProviderProgressEntry[],
): StaticSystemInstruction[] {
  const runStartEvent = getRunStartEvent(events);
  const modeName = readString(runStartEvent?.event_data.mode);
  const seenBodies = new Set<string>();

  return getPhasePrompts(events)
    .map((phasePrompt) => ({
      body: phasePrompt.systemPrompt.trim(),
      mode: modeName,
      phase: phasePrompt.phase,
      receivedAt: phasePrompt.receivedAt,
      stepNumber: phasePrompt.stepNumber,
      timestamp: phasePrompt.timestamp,
    }))
    .filter((instruction) => instruction.body.length > 0)
    .filter((instruction) => {
      if (seenBodies.has(instruction.body)) {
        return false;
      }

      seenBodies.add(instruction.body);
      return true;
    });
}
